import type { Device } from './traccar-api';
import { sendCommand } from './traccar-api';

// JT/T 1078 cameras (Jimi JC181 / JC371 / JC450) stream to our media server, which
// republishes each channel over WebRTC (WHEP) at media.n-gps.com.
const WEBRTC_BASE_URL = 'https://media.n-gps.com';
const MEDIA_STATUS_BASE = `${WEBRTC_BASE_URL}/media-status`;
const JT1078_SERVER = 'media-ip.n-gps.com';
const JT1078_PORT = 10002;

const CHANNELS_BY_DEVICE_TYPE: Record<number, number> = {
  55: 2, // JC181
  56: 3, // JC371
  57: 5, // JC450
};

const deviceType = (device: Device) => Number(device.attributes?.deviceType);

export const isJt1078Camera = (device: Device): boolean =>
  deviceType(device) in CHANNELS_BY_DEVICE_TYPE || Boolean(device.attributes?.cameraEnabled);

/**
 * Camera channels, 1-based as in the JT/T 1078 videoStart command. The media server's
 * WHEP and status paths are 0-based, so the helpers below convert (channel - 1).
 */
export const getJt1078Channels = (device: Device): number[] =>
  Array.from({ length: CHANNELS_BY_DEVICE_TYPE[deviceType(device)] ?? 2 }, (_, i) => i + 1);

export const getJt1078WhepUrl = (imei: string, channel: number) => `${WEBRTC_BASE_URL}/rtc/live/${channel - 1}/${imei}/whep`;

/** Asks the camera to start pushing the channel to the media server. */
export const startJt1078Streaming = (deviceId: number, channel: number) =>
  sendCommand({
    deviceId,
    type: 'videoStart',
    textChannel: false,
    attributes: { server: JT1078_SERVER, port: JT1078_PORT, index: channel },
  });

export interface Jt1078MediaStatus {
  connected?: boolean;
  publishing?: boolean;
  hasVideo?: boolean;
  videoAgeMs?: number;
}

export const getJt1078MediaStatus = async (imei: string, channel: number): Promise<Jt1078MediaStatus | null> => {
  try {
    const res = await fetch(`${MEDIA_STATUS_BASE}/status/live/${imei}/${channel - 1}`);
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  }
};

/** Waits until the media server receives video for the channel (or gives up). */
const FRESH_VIDEO_MS = 10000;

export const waitForJt1078Video = async (imei: string, channel: number, timeoutMs = 30000): Promise<boolean> => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const status = await getJt1078MediaStatus(imei, channel);
    // "publishing" can linger after the camera stops sending (e.g. ignition off): require a fresh frame.
    const fresh = status?.videoAgeMs == null || status.videoAgeMs < FRESH_VIDEO_MS;
    if (status?.publishing && status.hasVideo && fresh) return true;
    await new Promise((resolve) => setTimeout(resolve, 1500));
  }
  return false;
};
